import {DocumentPreview} from "@/components/ui/forms/DocumentPreview";
import {MaskList} from "@/components/ui/forms/MaskList";
import {EditSelect} from "@/components/ui/inputs/EditSelect";
import {RadioGroup} from "@/components/ui/inputs/RootRadio";
import {checkNameTaken, isEqual} from "@/helpers";
import {useCriteriaOptions, usePhaseOptions} from "@/hooks/ScenarioOptions";
import {DoctorField} from "@/pages/admin/DoctorPage/DoctorCreatePage";
import {
  clearCreateScenario,
  createScenario,
  current,
  setCriteriaWeights,
  setError,
  setIsChanged,
  setTarget,
} from "@/redux/slices/scenarioSlices/createSlice";

import {useCallback, useEffect, useRef, useState} from "react";

import check from "@images/badges/check.png";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {FillDataPopup} from "@popups/attentionPopups/FillDataPopup";
import {SuccessfulSavePopup} from "@popups/attentionPopups/SuccessfulSavePopup";
import {UploadFilePopup} from "@popups/uploadPopups/UploadFilePopup";
import {UploadImagePopup} from "@popups/uploadPopups/UploadImagePopup";
import {getDoctors} from "@redux/slices/doctorSlices/tableSlice";
import {
  clearOptions,
  getProducts,
} from "@redux/slices/scenarioSlices/optionsSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {ControlButton} from "@ui/buttons/ControlButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {EditField} from "@ui/forms/EditField";
import {EditTitle} from "@ui/forms/EditTitle";
import {ViewField} from "@ui/forms/ViewField";
import {BorderArticle} from "@ui/general/BorderArticle";
import {PreviewCard} from "@ui/general/PreviewCard";
import {Spinner} from "@ui/general/Spinner";
import {getScenarioCriteria, SCENARIO_CERTIFICATION_BADGES} from "@variables";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {v4 as uuidv4} from "uuid";

import styles from "./styles.module.scss";

export const ScenarioCreatePage = () => {
  const dispatch = useDispatch();
  const scenarioRef = useRef();
  const {openPopup} = usePopup();
  const {t} = useTranslation();

  const {isMobile, isMayoly} = useSelector((state) => state.common.settings);
  const {products} = useSelector((state) => state.scenario.options);
  const {doctors} = useSelector((state) => state.doctor.table);
  const {target, errors, status} = useSelector(
    (state) => state.scenario.create,
  );

  const [photoFile, setPhotoFile] = useState(null);
  const [selectedProductLineId, setSelectedProductLineId] = useState(null);
  const [fileList, setFileList] = useState([]);

  const criteriaOptions = useCriteriaOptions(target);
  const phaseOptions = usePhaseOptions();

  useEffect(() => {
    dispatch(getDoctors({page: 1, size: 500}));
  }, [dispatch]);

  const getFilteredProducts = useCallback(() => {
    if (!selectedProductLineId) {
      return products;
    }
    return products.filter(
      (product) => product.product_line_id === selectedProductLineId,
    );
  }, [products, selectedProductLineId]);

  const doctorOptions =
    Array.isArray(doctors) &&
    doctors
      .filter((d) => d.state === "completed")
      .map((d) => ({id: d.id, name: d.name}));

  const updateScenarioField = useCallback(
    (fieldType, value) => {
      dispatch(
        setTarget({
          ...target,
          [fieldType]: value,
        }),
      );
    },
    [target, dispatch],
  );

  const handleClickCover = useCallback(
    (color) => {
      updateScenarioField("mask", color);
    },
    [updateScenarioField],
  );

  const handleClickUploadImage = () => {
    openPopup(<UploadImagePopup setPhotoFile={setPhotoFile} />);
  };

  const handleClickUploadFile = () => {
    openPopup(
      <UploadFilePopup
        fileList={fileList}
        setFileList={setFileList}
        title={t("popup.upload_material_file.title")}
        subtitle={t("popup.upload_material_file.subtitle")}
      />,
    );
  };

  const handleClickRemoveFile = (fileName) => {
    setFileList((prevFileList) =>
      prevFileList.filter((file) => file.file.name !== fileName),
    );
  };

  const handleClickRemovePhoto = () => {
    setPhotoFile(null);
  };

  const handleSelect = (selectedOption) => {
    const productId = selectedOption.id;
    const currentProductIds = target.product_ids || [];

    if (currentProductIds.includes(productId)) {
      const newProductIds = currentProductIds.filter((id) => id !== productId);
      updateScenarioField("product_ids", newProductIds);

      if (newProductIds.length === 0) {
        setSelectedProductLineId(null);
      }
    } else {
      if (currentProductIds.length === 0) {
        setSelectedProductLineId(selectedOption.product_line_id);
      }
      updateScenarioField("product_ids", [...currentProductIds, productId]);
    }
  };

  const handleClickSave = async () => {
    const validation = [
      target?.product_ids.length === 0,
      target?.name === "",
      target?.mask === "",
      target?.purpose === "",
      target?.objectives === "",
      target?.due_date === "",
      ...(!isMayoly ? [target?.doctor_profile_id === null] : []),
    ];

    if (target?.certification) {
      validation.push(target?.badge === "");
      validation.push(target?.start_date === "");
      validation.push(target?.finish_date === "");
    }

    if (validation.some((item) => item)) {
      openPopup(<FillDataPopup />);
    } else {
      const scenario = {
        scenario: {
          product_ids: target?.product_ids,
          doctor_profile_id: target?.doctor_profile_id || undefined,
          name: target?.name,
          mask: target?.mask,
          badge: target?.badge,
          start_date: target?.start_date,
          finish_date: target?.finish_date,
          purpose: target?.purpose,
          objectives: target?.objectives,
          key_messages: target?.key_messages,
          keywords: target?.keywords,
          structure: target?.structure,
          script: target?.script,
          phase: target?.phase,
          questions: target?.questions,
          criteria_weights: target?.criteria_weights,
          certification: target?.certification,
          document_files: fileList,
          due_date: target?.due_date,
          cover: photoFile,
        },
      };
      const resultAction = await dispatch(createScenario({scenario}));

      if (createScenario.fulfilled.match(resultAction)) {
        openPopup(<SuccessfulSavePopup title={t("table.scenario")} />);

        dispatch(clearCreateScenario(isMayoly));
        setPhotoFile(null);
        setFileList([]);
      } else {
        dispatch(setError("Save error!"));
      }
    }
  };

  useEffect(() => {
    dispatch(getProducts());
    dispatch(setCriteriaWeights(getScenarioCriteria(isMayoly)));
  }, [dispatch, isMayoly]);

  useEffect(() => {
    return () => {
      dispatch(clearCreateScenario(isMayoly));
      dispatch(clearOptions());
      setSelectedProductLineId(null);
    };
  }, [dispatch, isMayoly]);

  useEffect(() => {
    dispatch(setIsChanged(!isEqual(target, current)));
  }, [target, current, dispatch]);

  useEffect(() => {
    return () => {
      if (photoFile) {
        URL.revokeObjectURL(photoFile);
      }
    };
  }, [photoFile]);

  useEffect(() => {
    if (scenarioRef.current && isMobile) {
      scenarioRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [isMobile]);

  return (
    <div
      ref={scenarioRef}
      className={styles.scenarioWrapper}
    >
      <BorderArticle>
        <div className={styles.scenario}>
          <BackButton />

          {status === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.scenarioContent}>
              <div className={styles.scenarioEditContent}>
                <div className={styles.scenarioContentHeadWrapper}>
                  <div className={styles.scenarioContentHead}>
                    <EditTitle
                      label={"scenario-name"}
                      value={target?.name}
                      placeholder={`${t("input.scenario_name_placeholder")}`}
                      disabledButton={false}
                      handleClickSave={() => handleClickSave()}
                      onChange={(e) =>
                        updateScenarioField("name", e.target.value)
                      }
                      error={
                        errors && checkNameTaken(errors)
                          ? t("server_error.exist_name")
                          : null
                      }
                    />

                    <ViewField
                      label={`${t("input.background")}*`}
                      customContent={
                        <MaskList
                          onClickCover={(color) => handleClickCover(color)}
                        />
                      }
                    />

                    <ViewField
                      label={t("input.cover")}
                      customContent={
                        <div
                          className={"flex flex-col items-start gap-[16rem]"}
                        >
                          {photoFile && (
                            <DocumentPreview
                              name={photoFile?.name}
                              onClickCross={() => handleClickRemovePhoto()}
                            />
                          )}

                          <ControlButton
                            color={"var(--violetLight)"}
                            txt={t("button.upload_image")}
                            onClick={() => handleClickUploadImage()}
                          />
                        </div>
                      }
                    />
                  </div>

                  <ViewField
                    label={t("input.preview")}
                    value={target?.name}
                    direction={"col"}
                    customContent={
                      <PreviewCard
                        name={target?.name}
                        mask={target?.mask}
                        image={
                          photoFile ? URL.createObjectURL(photoFile) : undefined
                        }
                      />
                    }
                  />
                </div>
              </div>

              <ul
                className={`${styles.scenarioContentBody} ${styles.scenarioEditBody}`}
              >
                <EditField
                  label={t("input.is_certification")}
                  checked={target?.certification}
                  onChange={(e) =>
                    updateScenarioField("certification", e.target.checked)
                  }
                  type={"checkbox"}
                />

                {target?.certification && (
                  <EditField
                    label={`${t("input.date_range")}`}
                    fromDate={
                      target?.start_date
                        ? typeof target.start_date === "string"
                          ? new Date(target.start_date)
                          : target.start_date
                        : new Date()
                    }
                    setFromDate={(date) => {
                      updateScenarioField(
                        "start_date",
                        date instanceof Date ? date.toISOString() : date,
                      );
                    }}
                    toDate={
                      target?.finish_date
                        ? typeof target.finish_date === "string"
                          ? new Date(target.finish_date)
                          : target.finish_date
                        : new Date()
                    }
                    setToDate={(date) => {
                      updateScenarioField(
                        "finish_date",
                        date instanceof Date ? date.toISOString() : date,
                      );
                    }}
                    type={"range_date"}
                  />
                )}

                <EditField
                  label={`${t("input.period_of_action")}*`}
                  setDate={(date) =>
                    updateScenarioField(
                      "due_date",
                      date instanceof Date ? date.toISOString() : date,
                    )
                  }
                  date={target?.due_date}
                  type={"date"}
                />

                {!isMayoly && (
                  <div className={""}>
                    <EditField
                      label={`${t("input.doctor")}*`}
                      type={"custom"}
                      height={150}
                      customContent={
                        <div
                          className={
                            "relative flex w-[500rem] items-center gap-[16rem]"
                          }
                        >
                          <EditSelect
                            key={"doctor_profile_id"}
                            resetKey={"doctor_profile_id"}
                            height={150}
                            searchPlaceholder={t("input.doctor_search")}
                            activeOption={
                              doctorOptions.find(
                                (d) => d.id === target?.doctor_profile_id,
                              )?.name
                            }
                            options={doctorOptions}
                            onSelect={(selectedOption) =>
                              updateScenarioField(
                                "doctor_profile_id",
                                selectedOption?.id ?? null,
                              )
                            }
                            emptyResult={false}
                            multiSelect={false}
                          />
                          <DoctorField
                            doctorProfileId={target?.doctor_profile_id}
                          />
                        </div>
                      }
                    />
                  </div>
                )}

                <EditField
                  label={`${t("input.related_products")}*`}
                  height={150}
                  searchPlaceholder={t("input.product_search")}
                  multiSelect
                  activeOption={
                    target?.product_ids && Array.isArray(target.product_ids)
                      ? target.product_ids
                          .map(
                            (id) =>
                              products?.find((product) => product.id === id)
                                ?.name,
                          )
                          .filter(Boolean)
                      : []
                  }
                  options={getFilteredProducts()}
                  onSelect={(selectedOption) => handleSelect(selectedOption)}
                  error={
                    errors && errors.product_ids
                      ? isMobile
                        ? t("server_error.same_product_line")
                        : t("server_error.same_product_line_full")
                      : null
                  }
                  type={"select"}
                />

                <EditField
                  label={`${t("input.purpose")}*`}
                  value={target?.purpose}
                  onChange={(e) =>
                    updateScenarioField("purpose", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.objectives")}*`}
                  value={target?.objectives}
                  onChange={(e) =>
                    updateScenarioField("objectives", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.messages")}*`}
                  value={target?.key_messages}
                  onChange={(e) =>
                    updateScenarioField("key_messages", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.words")}*`}
                  value={target?.keywords}
                  onChange={(e) =>
                    updateScenarioField("keywords", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.structure")}*`}
                  value={target?.structure}
                  onChange={(e) =>
                    updateScenarioField("structure", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.script")}*`}
                  value={target?.script}
                  onChange={(e) =>
                    updateScenarioField("script", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.objections")}*`}
                  value={target?.objections}
                  onChange={(e) =>
                    updateScenarioField("questions", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.materials")}`}
                  value={target?.documents}
                  type={"custom"}
                  customContent={
                    <div className={styles.documents}>
                      {fileList && fileList.length > 0 && (
                        <div className={styles.documentsPreview}>
                          {fileList.map((file) => (
                            <div key={uuidv4()}>
                              <DocumentPreview
                                name={file?.file?.name}
                                onClickCross={(name) =>
                                  handleClickRemoveFile(name)
                                }
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      <ControlButton
                        color={"var(--violetLight)"}
                        txt={t("button.upload_material")}
                        onClick={() => handleClickUploadFile()}
                      />
                    </div>
                  }
                />

                <EditField
                  label={`${t("input.phase")}*`}
                  value={target?.phase}
                  type={"custom"}
                  customContent={
                    <RadioGroup
                      name={"phase"}
                      className={styles.radioGroup}
                      options={phaseOptions}
                      value={target?.phase}
                      onChange={(option) => {
                        const phaseValue = option?.value || option;
                        updateScenarioField("phase", phaseValue);
                      }}
                    />
                  }
                />

                <EditField
                  label={`${t("input.criteria")}*`}
                  type={"custom"}
                  customContent={
                    <ul className={styles.criteriaContent}>
                      {criteriaOptions &&
                        criteriaOptions.length > 0 &&
                        criteriaOptions.map((item) => (
                          <li key={item.id}>
                            <RootDesc>
                              <b
                                dangerouslySetInnerHTML={{__html: item.label}}
                              />
                            </RootDesc>

                            <ul>
                              {item.rates.map((rate) => (
                                <li key={rate.id}>
                                  <SmallDesc>
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: rate.label,
                                      }}
                                    />
                                  </SmallDesc>

                                  <RadioGroup
                                    rating
                                    ratingType={"bullet"}
                                    required={false}
                                    name={item.value}
                                    value={rate.value}
                                    onChange={(ratingValue) => {
                                      const currentCriteria =
                                        target?.criteria_weights || {};
                                      const updatedCriteria = {
                                        ...currentCriteria,
                                        [item.value]: {
                                          ...currentCriteria[item.value],
                                          [rate.key]: ratingValue,
                                        },
                                      };

                                      dispatch(
                                        setTarget({
                                          ...target,
                                          criteria_weights: updatedCriteria,
                                        }),
                                      );
                                    }}
                                  />
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                    </ul>
                  }
                />

                {target?.certification && (
                  <EditField
                    label={`${t("input.badge")}*`}
                    value={target?.badge}
                    onChange={(e) =>
                      updateScenarioField("badge", e.target.value)
                    }
                    type={"custom"}
                    customContent={
                      <ul className={styles.badgeContent}>
                        {SCENARIO_CERTIFICATION_BADGES.map((badge) => (
                          <li
                            onClick={() =>
                              updateScenarioField("badge", badge.name)
                            }
                            style={
                              target?.badge === badge.name
                                ? {
                                    transform: "scale(1.1)",
                                    cursor: "default",
                                    pointerEvents: "none",
                                    boxShadow:
                                      "0 -10rem 10rem 0 rgba(180, 0, 226, 0.1) inset, 0 10rem 10rem 0 rgba(180, 0, 226, 0.1)",
                                  }
                                : {cursor: "pointer"}
                            }
                            key={badge.id}
                          >
                            <img
                              src={badge.image}
                              alt={badge.name}
                            />

                            <i
                              style={
                                target?.badge === badge.name
                                  ? {
                                      opacity: 1,
                                      transform: "scale(1)",
                                      right: "0",
                                      top: "0",
                                    }
                                  : {}
                              }
                            >
                              <img
                                src={check}
                                alt={"check"}
                              />
                            </i>
                          </li>
                        ))}
                      </ul>
                    }
                  />
                )}
              </ul>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
